import { cx, MarkerIcon } from "../shared/markers";
import { UiIcon } from "./Icon";
import { strings } from "./i18n";
import { SHEET_OFFERS, sheetTag } from "./offers";
import { sisterVars } from "./SisterCard";
import type { PartnerMapAction } from "./track";
import type { PartnerContact, PartnerMapContent, Place, SisterRestaurant } from "./types";
import styles from "./PartnerMap.module.scss";

interface VenuePanelProps {
	content: PartnerMapContent;
	contact: PartnerContact;
	/** a card of the offers tab was pressed: the page goes there */
	onOffer: (id: string) => void;
	onTrack: (action: PartnerMapAction, item?: string) => void;
}

/**
 * The body of a restaurant or bar's own sheet, under its address and name (`Sheet`): when it is open and what it is, the first of its offers (each leads to its
 * card in the offers tab), then "Book a table" and the menu.
 */
export const VenuePanel = ({ content, contact, onOffer, onTrack }: VenuePanelProps) => {
	const { base, offers } = content;
	const t = strings(content.lang);
	const text = [base.hours && `${base.hours}.`, base.blurb].filter(Boolean).join(" ");
	return (
		<>
			{text && <p>{text}</p>}
			{offers && (
				<div className={styles.mini}>
					{offers.cards.slice(0, SHEET_OFFERS).map((card) => (
						<button key={card.id} type="button" id={`partnermap-sheet-offer-${card.id}`} onClick={() => onOffer(card.id)}>
							{card.title}
							<span>{sheetTag(card, t)}</span>
						</button>
					))}
				</div>
			)}
			<div className={styles.acts}>
				<a
					id="partnermap-sheet-book"
					className={cx(styles.btn, styles.primary)}
					href={contact.book}
					target="_blank"
					rel="noopener"
					onClick={() => onTrack("contact", "book")}
				>
					<MarkerIcon name="p_restaurant" size={15} />
					{t.bookTable}
				</a>
				<a id="partnermap-sheet-menu" className={styles.btn} href={contact.menu} target="_blank" rel="noopener" onClick={() => onTrack("contact", "menu")}>
					<UiIcon name="menu" />
					{t.menu}
				</a>
			</div>
		</>
	);
};

interface SisterPanelProps {
	content: PartnerMapContent;
	sister: SisterRestaurant;
	place: Place;
	onTrack: (action: PartnerMapAction, item?: string) => void;
}

/** The body of the sister restaurant's sheet under its name: what it is, "Book a table" and its menu, then its other links and the way there. */
export const SisterPanel = ({ content, sister, place, onTrack }: SisterPanelProps) => {
	const t = strings(content.lang);
	// the buttons are in the sister's colour, which is the sheet's to set: it is not inside the card that sets it elsewhere
	const own = sisterVars(sister);
	return (
		<>
			{place.blurb && <p>{place.blurb}</p>}
			<div className={styles.acts} style={own}>
				<a
					id="partnermap-sheet-sister-book"
					className={cx(styles.btn, styles.sisterBtn)}
					href={sister.book}
					target="_blank"
					rel="noopener"
					onClick={() => onTrack("contact", "sister_book")}
				>
					<MarkerIcon name="p_restaurant" size={15} />
					{t.bookTable}
				</a>
				<a
					id="partnermap-sheet-sister-menu"
					className={styles.btn}
					href={sister.menu}
					target="_blank"
					rel="noopener"
					onClick={() => onTrack("contact", "sister_menu")}
				>
					<UiIcon name="menu" />
					{t.menu}
				</a>
			</div>
			<div className={cx(styles.acts, styles.actsNext)}>
				{sister.more?.map(({ label, url }) => (
					<a key={url} className={styles.btn} href={url} target="_blank" rel="noopener" onClick={() => onTrack("contact", "sister_more")}>
						{label}
					</a>
				))}
				<a className={styles.btn} href={place.googleUrl} target="_blank" rel="noopener" onClick={() => onTrack("place_map", place.slug)}>
					<UiIcon name="directions" />
					{t.directions}
				</a>
			</div>
		</>
	);
};
